import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { OnboardingStateService } from '../services/onboarding-state.service';
import { PermissionService } from '../../features/security/services/permission.service';

/**
 * Prevents access to /onboarding/* if onboarding is already completed.
 * Redirects to /dashboard.
 * Super admins are always redirected to /select-company (they don't onboard).
 * Exception: allows access when ?newCompany=true is set (user wants to create another company).
 */
export const onboardingReverseGuard: CanActivateFn = async (route) => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const stateService = inject(OnboardingStateService);
    const permissionService = inject(PermissionService);

    // Allow re-entry when explicitly creating a new company
    const isNewCompany = route.queryParams['newCompany'] === 'true';
    if (isNewCompany) {
        stateService.reset();
        return true;
    }

    // Super admins never access onboarding — check signal first, then fallback to Cognito
    let isSuperAdmin = permissionService.isSuperAdmin();
    if (!isSuperAdmin && !permissionService.initialized()) {
        try {
            const roles = await authService.getUserRoles();
            const superAdminRoles = ['superadmin', 'super_admin'];
            isSuperAdmin = roles.some(r => superAdminRoles.includes(r.toLowerCase()));
        } catch { }
    }
    if (isSuperAdmin) {
        return router.createUrlTree(['/select-company']);
    }

    try {
        const attributes = await authService.getUserAttributes();
        const hasCompleted = attributes['custom:hasCompletedOnboarding'] === 'true';

        if (hasCompleted) {
            return router.createUrlTree(['/dashboard']);
        }

        return true;
    } catch {
        return router.createUrlTree(['/login']);
    }
};
